import { FC, useEffect, useRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import { Modal, ModalProps } from 'modules/ui/modal';
import { Label } from 'modules/ui/label';
import { Alert } from 'modules/ui/alert';

import { CallData } from '../interfaces';
import { dateConfig } from 'modules/app/constants';
import { CALL_STATUS_COLOR } from '../constants';
import dayjs from 'dayjs';
import { callRecordingUrl } from '../hooks/crud';
import { useAuth } from 'modules/auth/hooks/useAuth';

interface Props extends ModalProps {
  call: CallData;
}

export const CallDetailsModal: FC<Props> = ({ open, onClose, call }) => {
  const { t } = useTranslation('common');
  const focusRef = useRef<HTMLDivElement>(null);

  const location = useMemo(() => window.location.origin, []);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { isAllAdmin, user } = useAuth();

  const hasTranscript = Array.isArray(call.transcript_data) && call.transcript_data.length > 0;

  useEffect(() => {
    if (open && focusRef.current) {
      focusRef.current.focus();
    }
  }, [open]);

  const fetchRecordingUrl = async () => {
    if (!call.recording_id || recordingUrl || loading) return;
    setLoading(true);
    try {
      const data = await callRecordingUrl(call.recording_id);
      setRecordingUrl(data.recording_url);
    } catch (err) {
      console.error('Failed to load recording URL:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('call.details.title')}>
      <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
        <Box flex="1 1 45%">
          <Typography variant="h6" gutterBottom tabIndex={-1} ref={focusRef}>
            {t('call.list.customerName')}:
          </Typography>
          <Typography variant="body1">{call.client_name || '-'}</Typography>
        </Box>

        <Box flex="1 1 45%">
          <Typography variant="h6" gutterBottom>
            {t('call.list.customerPhone')}:
          </Typography>
          <Typography variant="body1">{call.client_phone_number || '-'}</Typography>
        </Box>

        <Box flex="1 1 45%" mt={2}>
          <Typography variant="h6" gutterBottom>
            {t('call.list.callCreate')}:
          </Typography>
          <Typography variant="body1">
            {call.created_at ? dayjs(call.created_at).format(dateConfig.format.dateTime) : '-'}
          </Typography>
        </Box>

        <Box flex="1 1 45%" mt={2}>
          <Typography variant="h6" gutterBottom>
            {t('call.list.callStatus')}:
          </Typography>
          <Typography variant="body1">
            {call.status ? (
              <Label text={t(`call.status.${call.status}`)} color={CALL_STATUS_COLOR[call.status]} />
            ) : (
              '-'
            )}
          </Typography>
        </Box>
      </Box>

      <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
        <Box flex="1 1 45%" mt={2}>
          <Typography variant="h6" gutterBottom>
            {t('call.list.callStart')}:
          </Typography>
          <Typography variant="body1">
            {call.call_start_time ? dayjs(call.call_start_time).format(dateConfig.format.dateTime) : '-'}
          </Typography>
        </Box>

        <Box flex="1 1 45%" mt={2}>
          <Typography variant="h6" gutterBottom>
            {t('call.list.callEnd')}:
          </Typography>
          <Typography variant="body1">
            {call.call_end_time ? dayjs(call.call_end_time).format(dateConfig.format.dateTime) : '-'}
          </Typography>
        </Box>

        {isAllAdmin && (
          <Box flex="1 1 45%" mt={2}>
            <Typography variant="h6" gutterBottom>
              {t('call.list.technicianName')}:
            </Typography>
            <Typography variant="body1">{call.technician?.full_name || '-'}</Typography>
          </Box>
        )}

        <Box flex="1 1 45%" mt={2}>
          <Typography variant="h6" gutterBottom>
            {t('call.list.peopleJoined')}:
          </Typography>
          <Typography variant="body1">{call.call_max_participants || '-'}</Typography>
        </Box>
      </Box>



      {call.transcript_summary && (
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>
          {t('call.links.transcriptSummary') || 'Transcript Summary'}:
        </Typography>
        <Typography variant="body1" whiteSpace="pre-line">
          {call.transcript_summary}
        </Typography>
      </Box>
    )}


      {call.recording_id && (
        <Box mt={3}>
          <Accordion onChange={(_, expanded) => expanded && fetchRecordingUrl()}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ pl: 0, pr: 0, ml: 0 }}>
              <Typography variant="h6">{t('call.links.recordingLink')}:</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loading ? (
                <CircularProgress />
              ) : recordingUrl ? (
                <video
                  controls
                  style={{ width: '100%', borderRadius: 8 }}
                  src={recordingUrl}
                >
                  {t('call.links.recordingNotSupported')}
                </video>
              ) : (
                <Typography color="text.secondary">
                  {t('call.links.recordingUnavailable')}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

    
      {hasTranscript && (
        <Box mt={3}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ pl: 0, pr: 0, ml: 0 }}>
              <Typography variant="h6">{t('call.links.transcriptTitle') || 'Transcript'}:</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={1}>
                {call.transcript_data!.map((item, index) => {
                  const isSpeaker0 = item.speacker === 'Speaker 0';
                  const speakerLabel = isSpeaker0
                    ? t('call.links.speakerTechnician') || 'Technician'
                    : t('call.links.speakerClient') || 'Client';

                  return (
                    <Box
                      key={index}
                      display="flex"
                      justifyContent={isSpeaker0 ? 'flex-start' : 'flex-end'}
                    >
                      <Box
                        maxWidth="70%"
                        bgcolor={isSpeaker0 ? 'grey.200' : 'primary.light'}
                        color={isSpeaker0 ? 'black' : 'white'}
                        p={1.5}
                        borderRadius={3}
                        boxShadow={1}
                      >
                        <Typography variant="caption" display="block" gutterBottom>
                          {speakerLabel} — {item.start_time}
                        </Typography>
                        <Typography variant="body2">{item.words}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Modal>
  );
};
